import { DistributiveOmit } from 'src/constants/types';
import { RelationshipDirection } from 'src/containers/ProjectDiagram/constants/modelConcept';
import {
  MappingIdentifierPart,
  MappingIdentifierType,
} from 'src/containers/ProjectDiagram/types/data';
import {
  Model,
  ModelSnapshot,
} from 'src/containers/ProjectDiagram/types/model';
import { isModelConceptRelationship } from 'src/containers/ProjectDiagram/utils/data';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import {
  Suggestion,
  SuggestionType,
} from 'src/containers/ProjectMapping/types';
import {
  OWL_CLASS_IRI,
  OWL_DATATYPE_PROPERTY_IRI,
  OWL_OBJECT_PROPERTY_IRI,
  RDFS_LABEL_IRI,
  SCHEMA_ORG_DOMAIN_INCLUDES_IRI,
  SCHEMA_ORG_RANGE_INCLUDES_IRI,
  SUGGESTIONS_HAS_KEY,
  SUGGESTIONS_HAS_LABEL,
  SUGGESTIONS_HAS_PROPERTY_MATCH_IRI,
  SUGGESTIONS_KEY_MATCH,
  SUGGESTIONS_KEY_SCORE,
  SUGGESTIONS_KEY_SOURCE,
  SUGGESTIONS_LABEL_MATCH,
  SUGGESTIONS_LABEL_SCORE,
  SUGGESTIONS_LABEL_SOURCE,
  SUGGESTIONS_MATCH_SCORE_IRI,
  SUGGESTIONS_PROPERTY_MATCH_TYPE_IRI,
  SUGGESTIONS_SOURCE_IRI,
  SUGGESTIONS_TARGET_IRI,
  SUGGESTIONS_TARGET_PROPERTY_TYPE,
  SUGGESTIONS_TYPE_MATCH_IRI,
} from 'src/templates/queries/iris';
import { JsonLdResult } from 'src/templates/queries/types';
import { getSubClassesForIri } from 'src/utils/getSubClassesForIri';
import { arrayify } from 'src/utils/jsonld/arrayify';

const getRelationshipKey = (from: string, iri: string, to: string) =>
  `${from}-${iri}-${to}`;

const getSingle = <K extends string | number | boolean | undefined>(
  result: JsonLdResult[keyof JsonLdResult],
  key: string
): K | undefined => {
  if (Array.isArray(result)) {
    return getSingle(result[0], key);
  }
  switch (typeof result) {
    case 'object':
      return getSingle(result[key], key);
    case 'string':
      try {
        return decodeURIComponent(result) as K;
      } catch (error) {
        console.error(error);
        return undefined;
      }
    default:
      return (result || undefined) as K;
  }
};

const getMultiple = <K extends string | number | boolean | undefined>(
  result: JsonLdResult[keyof JsonLdResult],
  key: string
): K[] => {
  const results: K[] = [];
  const addValue = (val: JsonLdResult[keyof JsonLdResult]) => {
    const value = getSingle<K>(val, key);
    if (value !== undefined) {
      results.push(value);
    }
  };

  if (Array.isArray(result)) {
    result.forEach((res) => addValue(res));
  } else {
    addValue(result);
  }

  return results;
};

export type SuggestionWithoutResource = DistributiveOmit<
  Suggestion,
  'resource'
> & {
  /**
   * `source` is either the table name or the csv name depending on
   * what we sent for `sourceName` or `sourceTable`
   *
   * this is empty in the case that we're only providing a single resource
   */
  source: string | undefined;
};

export const parseSuggestionResults = (
  rawResults: JsonLdResult[],
  model: Model | ModelSnapshot | null,
  ignoreRelationships?: boolean
) => {
  const attributeIris = new Set();
  const relationshipKeys = new Set();

  if (model) {
    // build existing property keys from existing model
    Object.keys(model.modelData.attributesByIri).forEach((iri) =>
      attributeIris.add(iri)
    );

    model.linkDataArray.forEach((linkData) => {
      if (!isModelConceptRelationship(linkData)) {
        return;
      }

      // ignore circular relationships
      if (!linkData.from || !linkData.to || linkData.from === linkData.to) {
        return;
      }

      const domainIri = getModelConceptClassValue(linkData.from);
      const domains = [domainIri];
      getSubClassesForIri(model, domainIri).forEach((subClassIri) =>
        domains.push(subClassIri)
      );

      const rangeIri = getModelConceptClassValue(linkData.to);
      const ranges = [rangeIri];
      getSubClassesForIri(model, rangeIri).forEach((subClassIri) =>
        ranges.push(subClassIri)
      );

      domains.forEach((domain) => {
        ranges.forEach((range) => {
          relationshipKeys.add(
            getRelationshipKey(domain, linkData.value, range)
          );
        });
      });
    });
  } else {
    // build existing property keys from newly created model
    rawResults.forEach((result) => {
      const iri = getSingle<string>(result, '@id');
      if (!iri) {
        return;
      }

      const type = arrayify(result['@type']);
      if (type.includes(OWL_DATATYPE_PROPERTY_IRI)) {
        attributeIris.add(iri);
      }
      if (type.includes(OWL_OBJECT_PROPERTY_IRI) && !ignoreRelationships) {
        const domains = getMultiple<string>(
          result[SCHEMA_ORG_DOMAIN_INCLUDES_IRI],
          '@id'
        );

        const ranges = getMultiple<string>(
          result[SCHEMA_ORG_RANGE_INCLUDES_IRI],
          '@id'
        );

        if (domains && ranges) {
          domains.forEach((domain) => {
            ranges.forEach((range) => {
              // ignore circular relationships
              if (domain !== range) {
                relationshipKeys.add(getRelationshipKey(domain, iri, range));
              }
            });
          });
        }
      }
    });
  }

  /** label results */
  const labelMatches: Array<{
    field: string;
    score: number;
    source: string | undefined;
  }> = [];
  /** Primary identifier results */
  const primaryIdentifierMatches: Array<{
    fields: MappingIdentifierPart[];
    score: number;
    source: string | undefined;
  }> = [];
  /** TypeMatch results */
  const typeMatches: Array<{
    propertyMatchIds: string[];
    classIri: string;
    source: string | undefined;
    score: number;
  }> = [];
  /** PropertyMatch results by id */
  const propertyMatches: Record<
    string,
    {
      field: string;
      iri: string;
      propertyType?: string;
      score: number;
    }
  > = {};

  const classLabelsByIri: Record<string, string> = {};

  rawResults.forEach((result) => {
    const id = getSingle<string>(result, '@id');
    if (!id) {
      return;
    }

    const type = arrayify(result['@type']);

    if (type.includes(OWL_CLASS_IRI)) {
      const label = getSingle<string>(result[RDFS_LABEL_IRI], '@value');
      if (label) {
        classLabelsByIri[id] = label;
      }
    }

    if (type.includes(SUGGESTIONS_KEY_MATCH)) {
      const keySource = getSingle<string>(
        result[SUGGESTIONS_KEY_SOURCE],
        '@value'
      );
      const keyFields = getMultiple<string>(
        result[SUGGESTIONS_HAS_KEY],
        '@value'
      );
      const score = getSingle<number>(result[SUGGESTIONS_KEY_SCORE], '@value');

      if (keyFields?.length && score) {
        // Add underscore delimiter between each field of composite primary identifier
        const primaryKeyFields: MappingIdentifierPart[] = keyFields.flatMap(
          (field, idx, arr) =>
            arr.length - 1 !== idx
              ? [
                  { type: MappingIdentifierType.FIELD, value: field },
                  { type: MappingIdentifierType.DELIMITER, value: '_' },
                ]
              : { type: MappingIdentifierType.FIELD, value: field }
        );

        primaryIdentifierMatches.push({
          score,
          fields: primaryKeyFields,
          source: keySource,
        });
      }
    }

    if (type.includes(SUGGESTIONS_LABEL_MATCH)) {
      const labelSource = getSingle<string>(
        result[SUGGESTIONS_LABEL_SOURCE],
        '@value'
      );
      const labelField = getSingle<string>(
        result[SUGGESTIONS_HAS_LABEL],
        '@value'
      );
      const labelScore = getSingle<number>(
        result[SUGGESTIONS_LABEL_SCORE],
        '@value'
      );

      if (labelField && labelScore) {
        labelMatches.push({
          score: labelScore,
          field: labelField,
          source: labelSource,
        });
      }
    }

    const source = getSingle<string>(result[SUGGESTIONS_SOURCE_IRI], '@value');
    const target = getSingle<string>(result[SUGGESTIONS_TARGET_IRI], '@id');
    const targetPropertyType = getSingle<string>(
      result[SUGGESTIONS_TARGET_PROPERTY_TYPE],
      '@id'
    );
    const score = getSingle<number>(
      result[SUGGESTIONS_MATCH_SCORE_IRI],
      '@value'
    );

    if (target && score) {
      if (type.includes(SUGGESTIONS_PROPERTY_MATCH_TYPE_IRI)) {
        if (source && score) {
          propertyMatches[id] = {
            score,
            field: source,
            iri: target,
            propertyType: targetPropertyType,
          };
        }
      } else if (type.includes(SUGGESTIONS_TYPE_MATCH_IRI)) {
        const propertyMatchIds = arrayify(
          result[SUGGESTIONS_HAS_PROPERTY_MATCH_IRI]
        ).map((result) => getSingle(result, '@id')) as string[];

        typeMatches.push({
          propertyMatchIds,
          score,
          // `source` matches the value of what we sent for `sourceName` or `sourceTable`
          // this can be empty in the case that we're only providing a single resource
          source,
          classIri: target,
        });
      }
    }
  });

  const suggestions: SuggestionWithoutResource[] = [];

  labelMatches.forEach((labelMatch) => {
    suggestions.push({
      ...labelMatch,
      type: SuggestionType.LABEL,
    });
  });

  primaryIdentifierMatches.forEach((primaryIdentifierMatch) => {
    suggestions.push({
      ...primaryIdentifierMatch,
      type: SuggestionType.PRIMARY_IDENTIFIER,
    });
  });

  typeMatches.forEach(({ propertyMatchIds, classIri, source, score }) => {
    suggestions.push({
      iri: classIri,
      label: classLabelsByIri[classIri],
      score,
      source,
      type: SuggestionType.CLASS,
    });

    propertyMatchIds.forEach((id) => {
      if (!propertyMatches[id]) {
        return;
      }

      const { field, ...propertyMatch } = propertyMatches[id];
      if (attributeIris.has(propertyMatch.iri)) {
        suggestions.push({
          ...propertyMatch,
          classIri,
          classLabel: classLabelsByIri[propertyMatch.iri],
          field,
          source,
          type: SuggestionType.ATTRIBUTE,
        });
      } else if (propertyMatch.propertyType) {
        if (
          relationshipKeys.has(
            getRelationshipKey(
              classIri,
              propertyMatch.iri,
              propertyMatch.propertyType
            )
          )
        ) {
          suggestions.push({
            ...propertyMatch,
            classIri,
            classLabel: classLabelsByIri[propertyMatch.iri],
            direction: RelationshipDirection.OUTGOING,
            fields: [{ type: MappingIdentifierType.FIELD, value: field }],
            propertyTypeLabel: classLabelsByIri[propertyMatch.iri],
            source,
            type: SuggestionType.RELATIONSHIP,
          });
        }
        if (
          relationshipKeys.has(
            getRelationshipKey(
              propertyMatch.propertyType,
              propertyMatch.iri,
              classIri
            )
          )
        ) {
          suggestions.push({
            ...propertyMatch,
            classIri,
            classLabel: classLabelsByIri[propertyMatch.iri],
            direction: RelationshipDirection.INCOMING,
            fields: [{ type: MappingIdentifierType.FIELD, value: field }],
            propertyTypeLabel: classLabelsByIri[propertyMatch.iri],
            source,
            type: SuggestionType.RELATIONSHIP,
          });
        }
      }
    });
  });

  return suggestions;
};
